import React, { useEffect, useRef, useState } from 'react';

const PUB = process.env.PUBLIC_URL || '';
const HEART_URL = `${PUB}/heart.svg`;

const HeartFillIcon = ({ fillPercent = 0, instant = false }) => {
  const clamp = Math.max(0, Math.min(100, fillPercent));
  return (
    <div aria-hidden="true" style={{ flexShrink: 0, width: 30, height: 30, position: 'relative', overflow: 'hidden' }}>
      <img src={HEART_URL} alt="" width={30} height={30} style={{ opacity: 0.18, display: 'block' }} />
      <img src={HEART_URL} alt="" width={30} height={30} style={{
        position: 'absolute', left: 0, bottom: 0, display: 'block',
        clipPath: `inset(${100 - clamp}% 0 0 0)`,
        transition: instant ? 'none' : 'clip-path .45s cubic-bezier(.4,0,.2,1)',
      }} />
    </div>
  );
};

const ProgressBar = ({
  currentStep,
  totalSteps = 5,
  deco = false,
  live = false,
  ariaLabel = 'Progresso do cadastro',
}) => {
  const stepFloor = Math.max(0, ((currentStep - 1) / totalSteps) * 100);
  const stepCap = Math.min(100, (currentStep / totalSteps) * 100 - 3);
  const staticProgress = Math.min(100, (currentStep / totalSteps) * 100);
  const [liveProgress, setLiveProgress] = useState(stepFloor + 2);
  const prevStepRef = useRef(currentStep);

  useEffect(() => {
    prevStepRef.current = currentStep;
  }, [currentStep]);

  useEffect(() => {
    if (!live) return undefined;
    const hardFloor = stepFloor + 1.5;
    const hardCap = Math.max(hardFloor + 2, stepCap);
    const band = hardCap - hardFloor;
    const cruise = hardFloor + band * 0.86;
    const amplitude = Math.min(3.1, band * 0.16);
    setLiveProgress(hardFloor);
    let climb = 0;
    let last = performance.now();
    let raf = 0;
    const prefersReduce = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduce) {
      setLiveProgress(staticProgress);
      return undefined;
    }
    const tick = (now) => {
      const dt = Math.min(80, now - last) / 1000;
      last = now;
      climb = Math.min(1, climb + dt / 11);
      const eased = 1 - (1 - climb) * (1 - climb);
      const base = hardFloor + (cruise - hardFloor) * eased;
      const breath = Math.max(0, (climb - 0.88) / 0.12);
      const t = now / 1000;
      const wave = Math.sin((t * Math.PI * 2) / 3.6) * 0.72
        + Math.sin((t * Math.PI * 2) / 6.1) * 0.28;
      const next = Math.min(hardCap, Math.max(hardFloor, base + amplitude * breath * wave));
      setLiveProgress(next);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [live, currentStep, totalSteps, stepFloor, stepCap, staticProgress]);

  const progress = live ? liveProgress : staticProgress;

  return (
    <>
      <style>{`
        .sp {
          width: 100%;
          max-width: 400px;
          margin-bottom: 1.25rem;
          display: flex;
          align-items: center;
          gap: .6rem;
          /* Auditoria 13/05: 9 rage clicks/dia em ".sp" (telemetria descrevia
             como ".sp { width: 100% }" porque pegava o textContent do <style>
             irmao). Pacientes clicavam achando que navegavam etapas. cursor
             default + user-select none reforca que e indicador, nao botao. */
          cursor: default;
          user-select: none;
        }
        .sp__bar-area { flex: 1; }
        .sp__track {
          width: 100%;
          height: 5px;
          background: #E5E7EB;
          border-radius: 999px;
          overflow: hidden;
        }
        .sp--deco .sp__track {
          background: rgba(216,178,106,0.18);
          box-shadow: inset 0 0 0 1px rgba(181,138,79,0.18);
        }
        .sp__fill {
          height: 100%;
          background: var(--sp-color, #D32F2F);
          border-radius: 999px;
          transition: width .45s cubic-bezier(.4,0,.2,1);
        }
        .sp--live .sp__fill {
          transition: none;
          animation: sp-live-fade 2s ease-in-out infinite;
        }
        .sp__heart {
          color: var(--sp-color, #D32F2F);
          transition: transform .35s cubic-bezier(.4,0,.2,1);
          line-height: 0;
        }
        .sp--live .sp__heart {
          animation: sp-live-fade 2s ease-in-out infinite;
        }
        .sp__heart--pulse {
          animation: sp-pulse .4s ease-out;
        }
        .sp--live .sp__heart--pulse {
          animation: sp-pulse .4s ease-out, sp-live-fade 2s ease-in-out infinite;
        }
        @keyframes sp-pulse {
          0%   { transform: scale(1); }
          40%  { transform: scale(1.25); }
          100% { transform: scale(1); }
        }
        @keyframes sp-live-fade {
          0%, 100% { opacity: .55; }
          50% { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .sp--live .sp__fill,
          .sp--live .sp__heart,
          .sp--live .sp__heart--pulse {
            animation: none;
          }
        }
      `}</style>
      <div
        className={`sp${deco ? ' sp--deco' : ''}${live ? ' sp--live' : ''}`}
        role="progressbar"
        aria-label={ariaLabel}
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={`Etapa ${currentStep} de ${totalSteps}`}
      >
        <div className="sp__bar-area">
          <div className="sp__track">
            <div className="sp__fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
        <div className={`sp__heart${currentStep > prevStepRef.current ? ' sp__heart--pulse' : ''}`}>
          <HeartFillIcon fillPercent={progress} instant={live} />
        </div>
      </div>
    </>
  );
};

export default ProgressBar;
